<template>
  <v-row no-gutters>
    <v-col cols="12">
      <draggable-lists
        v-if="isFetched"
        :leftTitle="`admin.${scope}Layers`"
        rightTitle="admin.availableLayers"
        :leftList="groups"
        :rightList="searchedItems"
        :is-layers="true"
        @removedFromGroups="onRemovedFromGroups"
        @elementAdded="onElementAdded"
        @changeGroupName="openChangeGroupNameDialog"
        @addGroup="openAddGroupDialog"
        @manageLayer="openManageLayerDialog"
        @delete="deleteHandler"
        @deleteFromSystem="deleteFromSystemHandler"
        @changeSymbolization="openSymbolizationDialog"
        @manageCard="onManageCard"
        :disabled="disabled"
      >
        <template #button="{disabled}" v-if="scope === 'core'">
          <dots-menu
            font-size="19px"
            :disabled="disabled"
            :items="dotsItems"
            @WMSWMTSXYZ="openServiceLayer"
            @fromSource="openAddLayerFromSource"
          >
            <template #activator="{on}">
              <base-button v-on="on" translation-path="button.addLayer" class="mr-0 text-break"></base-button>
            </template>
          </dots-menu>
        </template>
        <template #button="{disabled}" v-else-if="hasAddLayerButton">
          <base-button
            translation-path="button.addLayer"
            @click="openAddLayerFromSource"
            class="mr-0"
            :disabled="disabled"
          ></base-button>
        </template>
        <template #search>
          <data-input
            :data-type="{ name: 'text' }"
            prepend-inner-icon="mdi-magnify"
            :placeholder="$i18n.t('default.find')"
            v-model="search"
            clearable
            height="32"
            hide-details
            :disabled="disabled"
            class="searchInput"
          >
          </data-input>
        </template>
        <template #listElement="element">
          <draggable-lists-layers-item
            :disabled="disabled"
            :key="element.id"
            v-bind="element"
            :dots-items="element.dotsItems"
            v-on="element.listeners"
          ></draggable-lists-layers-item>
        </template>
        <template #rightListElement="element">
          <draggable-lists-layers-item
            :disabled="disabled"
            :key="element.id"
            :is-right="true"
            v-bind="element"
            :level="1"
            :dots-items="element.dotsItems"
            @removedFromGroups="onRemovedFromGroups"
            @elementAdded="onElementAdded"
            @changeGroupName="openChangeGroupNameDialog"
            @addGroup="openAddGroupDialog"
            @manageLayer="openManageLayerDialog"
            @delete="deleteHandler"
            @deleteFromSystem="deleteFromSystemHandler"
            @changeSymbolization="openSymbolizationDialog"
            @manageCard="onManageCard"
          ></draggable-lists-layers-item>
        </template>
      </draggable-lists>
      <main-loader v-else></main-loader>
    </v-col>
    <main-dialog
      v-if="isDialogLoaded"
      :is-visible.sync="isDialogVisible"
      :title="$i18n.t(dialogTitle)"
      :body="$i18n.t(dialogBody, { name })"
      @agree="dialogAction"
      :loading="isConfirmButtonLoading"
      agree-text="dialog.agreeDelete"
    >
    </main-dialog>
    <style-dialog
      v-if="isStyleDialogLoaded && isStyleDialogVisible"
      :layer="currentLayerStyleEdit"
      :attributes="currentLayerStyleEditAttributes"
      :geomType="currentLayerStyleEditGeometryType"
      :is-visible.sync="isStyleDialogVisible"
      :loading="isStyleButtonLoading"
      @acceptStyle="setLayerStyle"
      :scope="scope"
      is-admin
    />
    <service-layer-adding-dialog
      v-if="isServiceLayerDialogLoaded"
      :is-visible.sync="isServiceLayerDialogVisible"
      :layer.sync="editedServiceLayer"
      :is-editing="isServiceLayerEditing"
    >
    </service-layer-adding-dialog>
    <layer-adding-dialog
      v-if="isAddLayerFromSourceLoaded"
      :is-visible.sync="isAddLayerFromSourceVisible"
      :layer.sync="editedVectorLayer"
      :is-editing="isVectorLayerEditing"
      :scope="scope"
    />
    <main-input-dialog
      dialog-width="40%"
      v-if="isInputDialogLoaded"
      :is-visible.sync="isInputDialogVisible"
      :title-translation="inputDialogTitle"
      :agree-text="inputDialogAgree"
      @agree="inputDialogAction"
      :inputs="addGroupInput"
      :loading="isAgreeButtonLoading"
    >
      <template #name.input="slot">
        <v-col>
          <v-row no-gutters>
            <v-col cols="12">
              <data-input v-model="name" v-bind="slot" v-on="$listeners" :rules="slot.rules"></data-input>
            </v-col>
          </v-row>
        </v-col>
      </template>
    </main-input-dialog>
    <manage-card-dialog
      v-if="isManageCardDialogLoaded"
      :is-visible.sync="isManageCardDialogVisible"
      :layerId="id"
      :layerName="name"
      :scope="scope"
    />
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import DraggableLists from '@/components/DraggableLists';
import DraggableListsLayersItem from '@/components/DraggableListsLayersItem';
import DotsMenu from '@/components/DotsMenu';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'DraggableListsLayers',
  components: {
    DotsMenu,
    MainLoader,
    DataInput,
    DraggableListsLayersItem,
    DraggableLists,
    ServiceLayerAddingDialog: () => import('@/components/ServiceLayerAddingDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainDialog: () => import('@/components/MainDialog'),
    StyleDialog: () => import('@/components/StyleDialog'),
    LayerAddingDialog: () => import('@/components/LayerAddingDialog'),
    ManageCardDialog: () => import('@/components/ManageCardDialog'),
  },
  props: {
    schema_scope: {
      type: String,
      default: 'core',
    },
    scope: {
      type: String,
      default: 'core',
    },
    layerDotsItems: {
      type: Array,
      default: () => [],
    },
    isFetched: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    search: '',
    name: undefined,
    id: undefined,
    layerType: undefined,
    isDialogLoaded: false,
    isDialogVisible: false,
    isInputDialogLoaded: false,
    isInputDialogVisible: false,
    isAgreeButtonLoading: false,
    isConfirmButtonLoading: false,
    isStyleDialogLoaded: false,
    isStyleDialogVisible: false,
    isManageCardDialogLoaded: false,
    isManageCardDialogVisible: false,
    isGroup: false,
    isEdit: false,
    currentLayerStyleEdit: {},
    currentLayerStyleEditAttributes: [],
    currentLayerStyleEditGeometryType: '',
    isServiceLayerDialogLoaded: false,
    isServiceLayerDialogVisible: false,
    isAddLayerFromSourceVisible: false,
    isAddLayerFromSourceLoaded: false,
    isStyleButtonLoading: false,
    dotsItems: [
      {
        name: 'fromSource',
      },
      {
        name: 'WMSWMTSXYZ',
      },
    ],
    nonPublicLayerDotsItems: [
      {
        name: 'manageCard',
      },
      {
        name: 'changeSymbolization',
      },
    ],
    editedVectorLayer: undefined,
    isVectorLayerEditing: false,
    editedServiceLayer: undefined,
    isServiceLayerEditing: false,
  }),
  computed: {
    searchedItems() {
      if (!this.search) {
        return this.sortItems();
      }
      return this.sortItems().filter(layer => layer.name.toLowerCase().includes(this.search.toLowerCase()));
    },
    inputDialogAction() {
      if (this.isGroup) {
        return this.isEdit ? this.editGroup : this.addGroup;
      }
      return this.editLayer;
    },
    inputDialogTitle() {
      return this.$i18n.t(this.isEdit ? 'dialog.title.manageLayer' : 'dialog.title.addDraggableGroup');
    },
    inputDialogAgree() {
      return this.isEdit ? 'button.save' : 'dialog.agreeAdd';
    },
    dialogTitle() {
      return `dialog.title.${this.isGroup ? 'deleteGroup' : 'deleteFromSystem'}`;
    },
    dialogBody() {
      return `dialog.body.${this.isGroup ? 'deleteLayersGroup' : 'deleteFromSystem'}`;
    },
    dialogAction() {
      return this.isGroup ? this.deleteLayerGroup : this.deleteLayer;
    },
    layers: get('layers/layersSchemaByScope@:scope.layers'),
    layersWithDotsItems() {
      return (
        this.layers.map(value => {
          return {
            ...value,
            ...(this.layerDotsItems.length > 0 && { dotsItems: this.layerDotsItems }),
          };
        }) || []
      );
    },
    groups: get('layers/layersSchemaByScope@:scope.groups'),
    layersAll: get('layers/layers'),
    addGroupInput() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.name',
          'validate-on-blur': !this.isEdit,
          rules: [v => !!v || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: 35,
          },
          hint: this.$i18n.t('dialog.newGroupFieldHint'),
        },
      ];
    },
    hasAddLayerButton() {
      const addLayerScopes = [
        'water',
        'sewer',
        'zdm_data',
        'masterplan_squares',
        'masterplan_streets',
        'masterplan_illumination',
      ];
      return addLayerScopes.includes(this.scope);
    },
  },
  watch: {
    isInputDialogVisible(nV) {
      if (!nV) {
        this.resetData();
      }
    },
    isDialogVisible(nV) {
      if (!nV) {
        this.resetData();
      }
    },
  },
  methods: {
    attachLayerGroup: call('layers/attachLayerGroup'),
    editLayerGroup: call('layers/editLayerGroup'),
    deleteLayerGroupAction: call('layers/deleteLayerGroup'),
    deleteLayerAction: call('layers/deleteLayer'),
    addNewLayersGroup: call('layers/addNewLayersGroup'),
    getLayerMetadata: call('layers/getLayerMetadata'),
    editLayerAction: call('layers/editLayer'),
    getLayersSchemaByScope: call('layers/getLayersSchemaByScope'),
    onRemovedFromGroups(e) {
      this.attachLayerGroup({
        layer_id: e.id,
        body: {
          group_id: null,
        },
        scope: this.scope,
        schema_scope: this.schema_scope,
      });
    },
    async editAddWrapper(action, payload) {
      try {
        this.isAgreeButtonLoading = true;
        await this[action](payload);
        setTimeout(() => {
          this.isAgreeButtonLoading = false;
          this.isInputDialogVisible = false;
        }, 500);
      } catch (error) {
        console.log(error);
        setTimeout(() => {
          this.isAgreeButtonLoading = false;
          this.isInputDialogVisible = false;
        }, 500);
      }
    },
    editLayer() {
      this.editAddWrapper('editLayerAction', {
        layer_id: this.id,
        layer_type: this.layerType,
        schema_scope: 'core',
        body: {
          name: this.name,
        },
        scope: this.scope,
      });
    },
    addGroup() {
      this.editAddWrapper('addNewLayersGroup', {
        schema_scope: this.schema_scope,
        name: this.name,
        layer_scope: this.scope,
      });
    },
    editGroup() {
      this.editAddWrapper('editLayerGroup', {
        group_id: this.id,
        body: {
          name: this.name,
        },
        scope: this.scope,
      });
    },
    deleteLayer() {
      this.handleConfirmationAgree('deleteLayerAction', {
        layer_type: this.layerType,
        layer_id: this.id,
        scope: this.scope,
      });
    },
    async deleteLayerGroup() {
      this.handleConfirmationAgree('deleteLayerGroupAction', {
        group_id: this.id,
        scope: this.scope,
        schema_scope: this.schema_scope,
      });
    },
    async handleConfirmationAgree(action, payload) {
      try {
        this.isConfirmButtonLoading = true;
        await this[action](payload);
      } catch (error) {
        console.log(error);
      }
      this.isDialogLoaded = false;
      this.isDialogVisible = false;
    },
    onManageCard(e) {
      this.id = e.id;
      this.name = e.name;
      this.isManageCardDialogLoaded = true;
      this.isManageCardDialogVisible = true;
    },
    setData(e) {
      const { id, name, type } = e;
      this.name = name;
      this.id = id;
      // xd
      this.layerType = type + 's';
    },

    async openSymbolizationDialog(e) {
      const l = this.layersAll[e.id];
      this.currentLayerStyleEditGeometryType = l.geometry_type;
      this.currentLayerStyleEdit = JSON.parse(
        JSON.stringify({
          style: l.style_web,
          layer_id: l.id,
          style_qgis_file_name: l.style_qgis_file_name,
          name: l.name,
          data_source_name: l.data_source_name,
        })
      );
      await Promise.resolve(this.getLayerMetadata({ dataSource: l.data_source_name })).then(value => {
        this.currentLayerStyleEditAttributes = value.metadata.attributes_schema.attributes
          .filter(a => a.data_type.name !== 'geometric')
          .map(a => {
            const isRelational = Boolean(a.relation);
            const dataType = a.data_type;
            const isDate = ['datetime', 'date', 'time'].includes(dataType.name);
            return {
              isRelational,
              valueLabel: isRelational ? `mvt_rel_${a.name}` : a.name,
              valueMvt: isDate ? `mvt_int_${a.name}` : a.name,
              value: a.name,
              text: a.verbose_name,
              dataType,
            };
          });
      });
      this.isStyleDialogVisible = true;
      this.isStyleDialogLoaded = true;
    },
    async setLayerStyle(e) {
      try {
        this.isStyleButtonLoading = true;
        const { layer_id, style } = e;
        await this.editLayerAction({
          layer_id,
          layer_type: 'features_layers',
          body: {
            style_web: style,
          },
          scope: this.scope,
        });
        this.isStyleButtonLoading = false;
      } catch (error) {
        console.log(error);
        this.isStyleButtonLoading = false;
      }
      this.isStyleDialogVisible = false;
    },
    openChangeGroupNameDialog(e) {
      this.isGroup = true;
      this.isEdit = true;
      this.setData(e);
      this.openInputDialog();
    },
    openAddGroupDialog() {
      this.isGroup = true;
      this.isEdit = false;
      this.openInputDialog();
    },
    openAddLayerFromSource() {
      this.isVectorLayerEditing = false;
      this.openAddLayerFromSourceDialog();
    },
    openAddLayerFromSourceDialog() {
      this.isAddLayerFromSourceVisible = true;
      this.isAddLayerFromSourceLoaded = true;
    },
    openManageLayerDialog(layer, layerDef) {
      this.isGroup = false;
      this.isEdit = true;
      if (layer.type === 'features_layer') {
        this.editedVectorLayer = { ...layerDef };
        this.isVectorLayerEditing = true;
        this.openAddLayerFromSourceDialog();
      } else {
        this.editedServiceLayer = { ...layerDef };
        this.isServiceLayerEditing = true;
        this.openServiceLayerDialog();
      }
    },
    openInputDialog() {
      this.isInputDialogVisible = true;
      this.isInputDialogLoaded = true;
    },
    openConfirmationDialog(e) {
      this.setData(e);
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    openServiceLayer() {
      this.isServiceLayerEditing = false;
      this.openServiceLayerDialog();
    },
    openServiceLayerDialog() {
      this.isServiceLayerDialogLoaded = true;
      this.isServiceLayerDialogVisible = true;
    },
    deleteFromSystemHandler(e) {
      this.isGroup = false;
      this.openConfirmationDialog(e);
    },
    deleteHandler(e) {
      this.isGroup = true;
      this.openConfirmationDialog(e);
    },
    onElementAdded(e) {
      const { toGroup, elementId, order_in_group, isGroup } = e;
      if (!isGroup) {
        this.attachLayerGroup({
          layer_id: elementId,
          body: {
            group_id: +toGroup,
            order_in_group: +order_in_group,
          },
          scope: this.scope,
          schema_scope: this.schema_scope,
        });
      } else {
        this.editAddWrapper('editLayerGroup', {
          schema_scope: this.schema_scope,
          group_id: e.elementId,
          body: {
            order_in_group,
          },
          scope: this.scope,
        });
      }
    },
    resetData() {
      this.isEdit = false;
      this.layerType = undefined;
      this.isGroup = false;
      this.name = undefined;
      this.isConfirmButtonLoading = false;
      this.isAgreeButtonLoading = false;
    },
    sortItems() {
      return this.layersWithDotsItems?.sort((a, b) => {
        if (a.type !== b.type) {
          return a.type.localeCompare(b.type);
        } else {
          return b.id - a.id;
        }
      });
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
